/* eslint-disable no-underscore-dangle */
// Pure, framework-free helpers for the Billingo invoicing flow.
//
// Hungarian fiscal documents, issued in sequence:
//   - Díjbekérő    (proforma / payment request)  — Billingo block "proforma"
//   - Előlegszámla (advance invoice)             — Billingo block "advance"
//   - Számla       (final invoice)               — Billingo block "invoice"
//
// Everything here is side-effect free so it can be unit-tested in isolation.
// The React layer (BillingoDocumentFlow et al.) and the mocked BillingoService
// build on top of these primitives.

import { getOrderLineKey } from './orderLineKey';
import { paidByOrderLine } from './orderLinePaid';

// Composite, globally-unique identity for an order line across many bookings.
// `order_line_key` (from getOrderLineKey) is booking-scoped; payments allocate
// against it, so invoiced and paid reconcile on the same key per booking.
export const lineKey = (bookingId, orderLineKey) => `${bookingId}|${orderLineKey}`;

export const DOC_TYPES = {
  DIJBEKERO: 'dijbekero',
  ELOLEGSZAMLA: 'elolegszamla',
  SZAMLA: 'szamla',
  // Synthetic type for legacy single invoices backfilled into the list so old
  // and new documents render uniformly. Never produced by the create flow.
  LEGACY: 'legacy',
};

// Display + Billingo-block metadata, keyed by DOC_TYPES value.
export const DOC_TYPE_META = {
  [DOC_TYPES.DIJBEKERO]: {
    value: DOC_TYPES.DIJBEKERO,
    hu: 'Díjbekérő',
    en: 'Proforma / payment request',
    billingoBlock: 'proforma',
    variant: 'secondary',
  },
  [DOC_TYPES.ELOLEGSZAMLA]: {
    value: DOC_TYPES.ELOLEGSZAMLA,
    hu: 'Előlegszámla',
    en: 'Advance invoice',
    billingoBlock: 'advance',
    variant: 'info',
  },
  [DOC_TYPES.SZAMLA]: {
    value: DOC_TYPES.SZAMLA,
    hu: 'Számla',
    en: 'Final invoice',
    billingoBlock: 'invoice',
    variant: 'success',
  },
  [DOC_TYPES.LEGACY]: {
    value: DOC_TYPES.LEGACY,
    hu: 'Számla',
    en: 'Legacy invoice',
    billingoBlock: 'invoice',
    variant: 'light',
  },
};

// The three user-selectable types, in issue order, for the type picker.
export const SELECTABLE_DOC_TYPES = [
  DOC_TYPES.DIJBEKERO,
  DOC_TYPES.ELOLEGSZAMLA,
  DOC_TYPES.SZAMLA,
];

// Hungarian VAT rates. 27% is the standard rate; 0% covers AAM / outside-scope
// transfers, which are common for cross-border transport. Kept editable per row.
//
// DEFAULT_VAT IS NOW THE LAST RESORT, NOT THE DEFAULT. Every line's default
// comes from the booking's own route (`suggestedVat` below); this constant is
// what a line gets when the route could NOT be established — no coordinates on
// the booking, or a stop nobody geocoded. There is one default path, not two:
// nothing else reads this.
export const DEFAULT_VAT = 27;
export const VAT_OPTIONS = [0, 5, 18, 27];

// THE RATE RULE IS NOT IN THIS REPO AND MUST NOT BE COPIED INTO IT.
//
// "Entirely within Hungary → 27%, any foreign part → 0%" is derived
// server-side, in ph-core's `Helpers/vatRate.helper.ts`, and arrives on each
// booking as `vat_suggestion: { rate, basis, confident }`. It lives there
// because the decision needs the endpoint coordinates and `programme.stops`
// and a point-in-polygon of Hungary's border — none of which belongs in a
// second copy here, and the polygon least of all (a bounding box contains
// Vienna, which inverts the answer on the busiest foreign route this business
// has). Read that helper's docblock for the rule, the polygon and the
// production disagreement figures before touching anything below.
//
// Everything here is presentation: which default a line starts on, and what
// the editor says when the operator picks something else.

// The booking's derived rate, or null when the route could not be established
// (`confident: false` — see the helper). An older API that does not send
// `vat_suggestion` reads as "not derived", i.e. exactly the behaviour that
// existed before this.
export function vatSuggestionOf(booking) {
  const suggestion = booking?.vat_suggestion;
  if (!suggestion || !suggestion.confident || typeof suggestion.rate !== 'number') return null;
  return suggestion;
}

// What a new line's VAT starts on: the route's rate when we have one, else the
// standard rate as before.
export function suggestedVat(booking) {
  return vatSuggestionOf(booking)?.rate ?? DEFAULT_VAT;
}

/**
 * What to say beside a line's VAT select, or null when nothing should be said.
 *
 * `{ tone: 'agrees' | 'differs', rate, basis }`. A HINT AND A WARNING, NEVER A
 * GATE: nothing in the flow reads this to disable a control, and nothing
 * should. The rule disagrees with 224 of the 1,455 document lines actually
 * issued in production (2026-11-02) and whether 140 of those were right is an
 * open question for the accountant — so an operator who overrides it is not
 * presumed wrong, and the submit path must stay open. This is deliberately NOT
 * routed through `finalInvoiceBlockedReason` / `FinalInvoiceBlockedAlert`,
 * which exist for the advance-refusal case and DO block.
 *
 * Returns null when the route was not derived: a warning over unknown data is
 * worse than silence.
 */
export function vatHint(item) {
  const suggestion = vatSuggestionOf(item);
  if (!suggestion) return null;
  const chosen = Number(item?.vat);
  return {
    tone: chosen === suggestion.rate ? 'agrees' : 'differs',
    rate: suggestion.rate,
    basis: suggestion.basis,
  };
}

// Default fiscal line name on Billingo documents — the standardised service
// category. The specific order-line name travels in the per-line `comment`.
export const DEFAULT_LINE_DESCRIPTION = 'Egyéb szállítást kiegészítő szolgáltatás '
  + '/ Other Transportation Support Activities';

// Experience bookings get a different fiscal category.
export const EXPERIENCE_LINE_DESCRIPTION = 'Programok szervezése / Organizing programs';

// The standardised fiscal description for a booking, by type.
export function defaultLineDescription(booking) {
  return booking?.booking_type === 'experience'
    ? EXPERIENCE_LINE_DESCRIPTION
    : DEFAULT_LINE_DESCRIPTION;
}

export function docTypeLabel(type) {
  return DOC_TYPE_META[type]?.hu || type;
}

// Customer-facing net amount of a single order_line: what the customer owes
// (debit) less anything credited back (discounts). Operator-only ledger lines
// (credit_operator) are not customer-facing and are filtered upstream.
export function orderLineAmount(line) {
  if (!line) return 0;
  return (Number(line.debit) || 0) - (Number(line.credit) || 0);
}

// True for order_lines that represent something the customer is billed for.
// Pure operator-cost / operator-cancellation lines carry no debit/credit and
// must not appear on a customer invoice.
export function isCustomerFacingLine(line) {
  return orderLineAmount(line) !== 0;
}

// Format a date as YYYY-MM-DD (best-effort; '' when unparseable).
function commentDate(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

// Billingo document languages (exact DocumentLanguage enum), default English.
export const DOCUMENT_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'us', label: 'English (US)' },
  { value: 'hu', label: 'Hungarian (Magyar)' },
  { value: 'de', label: 'German (Deutsch)' },
  { value: 'fr', label: 'French (Français)' },
  { value: 'it', label: 'Italian (Italiano)' },
  { value: 'ro', label: 'Romanian (Română)' },
  { value: 'sk', label: 'Slovak (Slovenčina)' },
  { value: 'hr', label: 'Croatian (Hrvatski)' },
];

// Billingo payment methods offered in the UI (value = Billingo enum).
export const PAYMENT_METHODS = [
  { value: 'wire_transfer', label: 'Bank transfer (átutalás)' },
  { value: 'bankcard', label: 'Card (bankkártya)' },
  { value: 'online_bankcard', label: 'Online card' },
  { value: 'cash', label: 'Cash (készpénz)' },
  { value: 'paypal', label: 'PayPal' },
];

// Earliest trip date across the bookings (the service date for a Számla).
function earliestTripDate(bookings) {
  const times = (bookings || [])
    .map((b) => b && (b.date_datetime || b.date))
    .filter(Boolean)
    .map((d) => new Date(d).getTime())
    .filter((t) => !Number.isNaN(t));
  return times.length ? commentDate(new Date(Math.min(...times))) : '';
}

// Most recent completed inbound payment for the set + whether any was by card
// (a Stripe charge id is our card signal).
function settlementPayment(payments) {
  const inbound = (payments || [])
    .filter((p) => p && p.direction === 'in' && p.status === 'completed')
    .sort((a, b) => new Date(b.occurred_at || 0) - new Date(a.occurred_at || 0));
  return {
    date: inbound[0] ? commentDate(inbound[0].occurred_at) : '',
    isCard: inbound.some((p) => p.external_refs && p.external_refs.stripe_charge_id),
  };
}

// What the default fulfillment date MEANS, per type, in the words the review
// step shows the operator. Lives here, immediately above the function that
// decides it, because a pre-fill panel that says "the trip date" while
// `defaultInvoiceMeta` quietly uses something else is the kind of true-looking
// sentence this screen already had too many of. Change the two together.
export const FULFILLMENT_DATE_SOURCE = {
  [DOC_TYPES.DIJBEKERO]: 'today',
  [DOC_TYPES.ELOLEGSZAMLA]: 'the payment date',
  [DOC_TYPES.SZAMLA]: 'the trip date',
};

// Type-aware invoice meta the user can override on the review step:
//   • fulfillment date — payment date (Előlegszámla), trip date (Számla),
//     today (Díjbekérő)
//   • payment method   — card when a card payment exists, else bank transfer
//   • due date         — today + 8 days
//   • payment date     — the settlement payment's date (if any)
export function defaultInvoiceMeta(docType, bookings = [], payments = []) {
  const today = commentDate(new Date());
  const dueDate = commentDate(new Date(Date.now() + 8 * 24 * 60 * 60 * 1000));
  const { date: paymentDate, isCard } = settlementPayment(payments);
  const trip = earliestTripDate(bookings);

  let fulfillment = today;
  if (docType === DOC_TYPES.ELOLEGSZAMLA) fulfillment = paymentDate || today;
  else if (docType === DOC_TYPES.SZAMLA) fulfillment = trip || today;

  return {
    fulfillment_date: fulfillment,
    due_date: dueDate,
    payment_method: isCard ? 'bankcard' : 'wire_transfer',
    language: 'en',
    // A Díjbekérő is a payment *request* — nothing is paid yet, so no date.
    payment_date: docType === DOC_TYPES.DIJBEKERO ? '' : paymentDate,
  };
}

// Build the per-line Billingo comment: the order-line's own description,
// enriched on the primary (base) line with booking context —
//   • Experience → the experience (programme) name
//   • Transfer   → the vehicle category, plus "Hourly Rental" when the booking
//                  has a programme/stops (the route is already in the
//                  order-line description, so it's not re-added).
// The booking date is always appended. Add-on (service/etc.) lines keep just
// their own description.
export function buildLineComment(booking, line) {
  const desc = (line?.description || '').trim();
  if (!booking || (line?.kind && line.kind !== 'base')) return desc;

  const parts = [];
  if (booking.booking_type === 'experience') {
    if (booking.programme?.name) parts.push(booking.programme.name);
  } else {
    if (booking.category?.name) parts.push(booking.category.name);
    const hasProgramme = Array.isArray(booking.programme?.stops)
      && booking.programme.stops.length > 0;
    if (hasProgramme) parts.push('Hourly Rental');
  }
  const date = commentDate(booking.date_datetime || booking.date);
  if (date) parts.push(date);

  return [desc, parts.join(', ')].filter(Boolean).join(' – ');
}

// Map a single booking's customer-facing order_lines into editable invoice
// line items. Each item keeps a stable `key` (booking id + line index) and the
// originating `order_reference` so a multi-booking document stays traceable.
export function bookingToLineItems(booking) {
  if (!booking || !Array.isArray(booking.order_lines)) return [];
  const ref = booking.booking_reference || booking._id;
  return booking.order_lines
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => isCustomerFacingLine(line))
    .map(({ line, index }) => {
      const orderLineKey = getOrderLineKey(line, index);
      return {
        // `key` is unique across bookings (selection/editing); `source_key` is
        // the booking-scoped order_line_key that payments allocate against.
        key: lineKey(booking._id || ref, orderLineKey),
        source_key: orderLineKey,
        booking_id: booking._id,
        order_reference: ref,
        // Standardised fiscal name (experience vs transport); the specific
        // order-line name + booking context goes in comment.
        description: defaultLineDescription(booking),
        comment: buildLineComment(booking, line),
        quantity: 1,
        // Order-line amount is the all-in price the customer pays (VAT
        // inclusive); the invoice backs VAT out of it.
        unit_price: orderLineAmount(line),
        // PER-BOOKING, WHICH IS PER-ROW FOR FREE. The rate is a property of the
        // trip, so it is derived once per booking and every line of that
        // booking starts on it: a one-order document gets one rate, a
        // multi-order document gets a different default on the rows of each
        // booking, with no grouping beyond the `booking_id` the line already
        // carries.
        vat: suggestedVat(booking),
        // The derivation travels WITH the line so the editor can say what the
        // route implies next to the select the operator is about to change,
        // without a second lookup or a parallel map keyed by booking. The
        // create payload drops it (the API's LineItemSchema is a non-strict
        // z.object, so unknown keys are stripped) — same as `key` and
        // `source_kind`, which have always travelled this way.
        vat_suggestion: booking.vat_suggestion,
        currency: line.currency || booking.currency || 'EUR',
        source_kind: line.kind || 'base',
        deduction: false,
      };
    });
}

// Flatten many bookings into one combined, editable line-item list.
export function bookingsToLineItems(bookings) {
  if (!Array.isArray(bookings)) return [];
  return bookings.flatMap((b) => bookingToLineItems(b));
}

// Per-line money math. VAT-INCLUSIVE: unit_price is the gross (all-in) price
// the customer pays, so gross == price * qty and the net is backed out. This
// keeps invoiced amounts in the same money terms as order-line prices and
// payments, so owed / invoiced / paid reconcile cleanly.
export function lineGross(item) {
  return (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
}
export function lineNet(item) {
  return lineGross(item) / (1 + (Number(item.vat) || 0) / 100);
}
export function lineVatAmount(item) {
  return lineGross(item) - lineNet(item);
}

// Totals grouped by currency — a single Billingo document is single-currency,
// so the UI warns when a selection mixes currencies. Returns
// { byCurrency: { EUR: {net,vat,gross}, ... }, currencies: ['EUR', ...] }.
export function documentTotals(items) {
  const byCurrency = {};
  (items || []).forEach((item) => {
    const cur = item.currency || 'EUR';
    if (!byCurrency[cur]) byCurrency[cur] = { net: 0, vat: 0, gross: 0 };
    byCurrency[cur].net += lineNet(item);
    byCurrency[cur].vat += lineVatAmount(item);
    byCurrency[cur].gross += lineGross(item);
  });
  return { byCurrency, currencies: Object.keys(byCurrency) };
}

// AN ADVANCE THAT HAS ALREADY BEEN SPENT.
//
// Billingo records the parent→child link ONLY ON THE CHILD: a Számla names
// the advances it nets off, an advance never names what spent it. So an
// advance with no link looks free whether it is or not, and re-clicking
// "Create Számla" cannot discover otherwise — Billingo simply refuses again,
// in its own words, after the commit:
//
//   Can not use advance(s) (137491737) for this document because it has
//   descendant(s)!
//
// Measured in production 2026-11-02: 14 of 19 recorded document failures are
// that one message; one order group absorbed NINETEEN attempts and was still
// uninvoiced; 10 of 30 failed work items had never been invoiced at all.
//
// The answer is resolved server-side and arrives on the Előlegszámla as
// `advance_consumption` — ABSENT when the advance is still spendable, which is
// why the test here is a plain truthiness check and there is no second flag to
// keep in step. See ph-core/src/Helpers/advanceConsumption.helper.ts for the
// two evidence kinds ('deducted' — a Számla we hold; 'refused' — Billingo's
// word alone) and why they must not be collapsed.
//
// A document from an API that predates the field has no `advance_consumption`
// and so reads as spendable: the same behaviour as before this existed.
export const advanceConsumption = (advance) => (advance && advance.advance_consumption) || null;

// Why a final Számla must not be issued from here at all, or '' when it may
// be — one sentence, for a disabled button's tooltip, mirroring
// finalInvoiceBlockedReason in tripClaims.js.
//
// ONLY the 'refused' kind blocks, and the asymmetry is the whole point:
//   • 'deducted' — we hold the Számla that netted the advance off. Dropping
//     the deduction is correct and the rest of the document can still go out.
//   • 'refused'  — a descendant exists in Billingo that we do NOT hold. The
//     trip may already be finally invoiced there, so issuing a full-price
//     Számla would double-charge the customer. Excluding the deduction and
//     carrying on is the WRONG recovery; the document has to be reconciled
//     against Billingo first.
export function advanceBlockedReason(priorAdvances = []) {
  const refused = (priorAdvances || [])
    .filter((a) => advanceConsumption(a)?.by === 'refused');
  if (!refused.length) return '';
  const numbers = refused.map((a) => a.number).filter(Boolean).join(', ');
  return 'Billingo already holds a document against '
    + `${refused.length === 1 ? `advance ${numbers}` : `advances ${numbers}`}`
    + ' that we do not have. Issuing a Számla now could invoice the trip twice —'
    + ' reconcile it in Billingo first.';
}

// Build the line set for a Számla: the selected (full) lines plus one negative
// deduction line per prior Előlegszámla, so the document gross equals the
// outstanding difference still owed after advances.
export function buildSzamlaWithDeductions(items, priorAdvances, currency = null) {
  const base = Array.isArray(items) ? [...items] : [];
  const deductions = (priorAdvances || [])
    // A spent advance is dropped HERE rather than disabled in the review
    // panel, so no caller can submit the deduction — Billingo refuses it, and
    // a deduction that cannot succeed has no business being in the line set a
    // human is asked to confirm. BillingoAdvancesCard says which advance went
    // and why; advanceBlockedReason decides whether the document may go out
    // without it at all.
    .filter((doc) => !advanceConsumption(doc))
    // Only deduct advances issued in the document's currency — a Billingo
    // document is single-currency, and an advance in another currency can't be
    // netted off here.
    .filter((doc) => !currency || (doc.currency || 'EUR') === currency)
    .map((doc) => ({
      key: `deduction:${doc.id || doc.number}`,
      booking_id: null,
      order_reference: doc.order_reference || null,
      description: `Levonás – Előlegszámla ${doc.number || ''}`.trim(),
      quantity: 1,
      unit_price: -(Number(doc.total) || 0),
      vat: 0,
      currency: doc.currency || 'EUR',
      source_kind: 'advance_deduction',
      deduction: true,
    }));
  return [...base, ...deductions];
}

// Statuses of a document that is NOT live — mirrors the API's
// NON_LIVE_INVOICE_STATUSES. A cancelled (sztornózott) document was voided at
// the tax authority and pending/failed ones were never issued, so none of them
// may be deducted from a final Számla or counted as invoiced. Deducting the
// cancelled 2026-001149 alongside its reissue 2026-001287 is what made the
// végszámla for PU_WEB_6270252 come to -290 EUR instead of 0.
const NON_LIVE_DOC_STATUSES = new Set(['cancelled', 'pending', 'failed']);

export function isLiveDocument(doc) {
  return !!doc && !NON_LIVE_DOC_STATUSES.has(doc.status);
}

// Document types that count toward an order's "invoiced" total. A Díjbekérő
// (proforma) is only a payment *request* — it books no VAT and is tracked
// separately as "requested", not "invoiced".
export const INVOICED_DOC_TYPES = [DOC_TYPES.ELOLEGSZAMLA, DOC_TYPES.SZAMLA];

// ADVANCES A DÍJBEKÉRŐ→SZÁMLA CONVERSION WOULD NOT DEDUCT.
//
// `createFromProforma` asks Billingo to turn a Díjbekérő into a Számla
// (POST /documents/{id}/create-from-proforma). Billingo copies the source
// document and is sent NO `advance_invoice`, and our side stores no
// `advance_invoice_ids` for it — verified in
// ph-core/src/Services/BillingoInvoiceService.ts → createFromProforma, and in
// BillingoClient.createDocumentFromProforma, which forwards only the dates and
// the comment. So the conversion CANNOT net an advance off.
//
// That matters because the row action offering it (useDocumentActions) hides
// itself only once the Díjbekérő is `converted`, and `proformaConversion`
// counts a Díjbekérő as converted when a live SZÁMLA covers its bookings — an
// Előlegszámla does not make it converted. The live window is therefore
// exactly "Díjbekérő issued, advance taken, no final Számla yet", which is the
// Needs-final-Számla queue.
//
// WHY THIS WARNS AND DOES NOT BLOCK, unlike advanceBlockedReason. Whether the
// conversion is wrong depends on what the Díjbekérő bills, and we cannot tell:
//   • Díjbekérő for the FULL amount, then an advance for the same money — the
//     conversion invoices the trip twice.
//   • Díjbekérő for the BALANCE after a deposit advance — the conversion is
//     correct and no deduction is wanted, because the advance already booked
//     its own VAT.
// Both shapes look identical in our data. The arithmetic is what the operator
// needs; the judgement is hers. Encoding a guess here would be worse than
// saying nothing, which is what the screen does today.
//
// Returns the live advances issued NO EARLIER than the Díjbekérő and sharing
// a booking with it. The time ordering mirrors `proformaConversion`'s, for its
// reason: a Díjbekérő raised AFTER an advance bills something that advance did
// not, such as an extra.
export function unDeductedAdvances(proforma, documents = []) {
  if (!proforma || proforma.type !== DOC_TYPES.DIJBEKERO) return [];
  const bookings = new Set((proforma.booking_ids || []).map(String));
  if (bookings.size === 0) return [];
  const since = proforma.created_at ? new Date(proforma.created_at).getTime() : -Infinity;
  return (documents || []).filter((d) => d
    && d.type === DOC_TYPES.ELOLEGSZAMLA
    && isLiveDocument(d)
    && (!d.created_at || new Date(d.created_at).getTime() >= since)
    && (d.booking_ids || []).some((id) => bookings.has(String(id))));
}

// Full customer-facing value of a booking (gross), in its primary currency.
// This is the denominator for "invoiced / remaining".
export function bookingValue(booking) {
  const totals = documentTotals(bookingToLineItems(booking));
  const currency = totals.currencies[0] || booking?.currency || 'EUR';
  return { gross: totals.byCurrency[currency]?.gross || 0, currency };
}

// Allocate a document's net total across the bookings it bills, proportional to
// each booking's positive (non-deduction) line gross. Stored on the document at
// creation so per-booking invoiced totals survive multi-booking documents and
// advance deductions (advance + final-net == full booking value).
export function bookingBreakdown(lineItems, netTotal) {
  const byBooking = {};
  (lineItems || [])
    .filter((i) => !i.deduction && i.booking_id)
    .forEach((i) => { byBooking[i.booking_id] = (byBooking[i.booking_id] || 0) + lineGross(i); });
  const totalPositive = Object.values(byBooking).reduce((a, b) => a + b, 0);
  const result = {};
  Object.entries(byBooking).forEach(([id, gross]) => {
    result[id] = totalPositive ? (gross / totalPositive) * netTotal : 0;
  });
  return result;
}

// Aggregate a set of documents into per-booking { requested, invoiced } totals.
// Pass DEDUPED documents (a multi-booking document attributes itself correctly
// via its booking_breakdown, so it must be counted once). A plain sum is
// right: the API stores a Számla's breakdowns NET of the advances it deducts
// (what it charges), so an Előlegszámla plus its Számla add up to the
// booking's value once.
export function invoicedByBooking(documents) {
  const acc = {};
  (documents || []).filter(isLiveDocument).forEach((doc) => {
    const isInvoiced = INVOICED_DOC_TYPES.includes(doc.type);
    const isRequested = doc.type === DOC_TYPES.DIJBEKERO;
    if (!isInvoiced && !isRequested) return;
    Object.entries(doc.booking_breakdown || {}).forEach(([id, amount]) => {
      if (!acc[id]) acc[id] = { requested: 0, invoiced: 0 };
      if (isInvoiced) acc[id].invoiced += amount;
      if (isRequested) acc[id].requested += amount;
    });
  });
  return acc;
}

// Roll a set of bookings + their documents into a summary table: one row per
// booking (value / requested / invoiced / remaining) plus combined totals.
// Currency is assumed consistent per booking; mixed-currency sets keep each
// booking's own currency and the totals are grouped by currency.
export function invoicedSummary(bookings, documents) {
  const byBooking = invoicedByBooking(documents);
  const rows = (bookings || []).map((booking) => {
    const value = bookingValue(booking);
    const agg = byBooking[booking._id] || { requested: 0, invoiced: 0 };
    return {
      booking_id: booking._id,
      order_reference: booking.booking_reference || booking._id,
      currency: value.currency,
      value: value.gross,
      requested: agg.requested,
      invoiced: agg.invoiced,
      remaining: value.gross - agg.invoiced,
    };
  });
  const totals = {};
  rows.forEach((r) => {
    if (!totals[r.currency]) {
      totals[r.currency] = {
        value: 0, requested: 0, invoiced: 0, remaining: 0,
      };
    }
    totals[r.currency].value += r.value;
    totals[r.currency].requested += r.requested;
    totals[r.currency].invoiced += r.invoiced;
    totals[r.currency].remaining += r.remaining;
  });
  return { rows, totals };
}

// Allocate a document's net total across the individual order lines it bills,
// keyed by the composite line key, proportional to each line's gross. Stored on
// the document so per-line invoiced totals survive multi-line/multi-booking docs.
export function lineBreakdown(lineItems, netTotal) {
  const byLine = {};
  (lineItems || [])
    .filter((i) => !i.deduction && i.key)
    .forEach((i) => { byLine[i.key] = (byLine[i.key] || 0) + lineGross(i); });
  const total = Object.values(byLine).reduce((a, b) => a + b, 0);
  const result = {};
  Object.entries(byLine).forEach(([key, gross]) => {
    result[key] = total ? (gross / total) * netTotal : 0;
  });
  return result;
}

// Per-line { requested, invoiced } across a deduped document set, keyed by the
// composite line key. Only Előlegszámla + Számla count as invoiced.
export function invoicedByLine(documents) {
  const acc = {};
  (documents || []).filter(isLiveDocument).forEach((doc) => {
    const isInvoiced = INVOICED_DOC_TYPES.includes(doc.type);
    const isRequested = doc.type === DOC_TYPES.DIJBEKERO;
    if (!isInvoiced && !isRequested) return;
    Object.entries(doc.line_breakdown || {}).forEach(([key, amount]) => {
      if (!acc[key]) acc[key] = { requested: 0, invoiced: 0 };
      if (isInvoiced) acc[key].invoiced += amount;
      if (isRequested) acc[key].requested += amount;
    });
  });
  return acc;
}

// Money actually received against a booking, split per order_line_key.
//
// THE RULE IS NOT HERE. It is `paidByOrderLine` in `orderLinePaid.js`, which
// mirrors ph-core's `OrderLinePaid.helper`; this function only decides WHICH
// LINES the money may land on and in WHAT ORDER — the booking's customer-
// facing order lines, in `order_lines` order, so the pool is spent the way
// the ledger reads on screen.
//
// It used to credit only a payment line that named an `order_line_key`, and
// report the rest as `unallocated`. That is why PU_DASH_5580294 — 360 EUR,
// two customers paying 180 each on their own cards, one payment keyed onto
// `base` and one never keyed — showed the base line as 180 of 360, called the
// végszámla partially paid, and told the office "180 paid but not allocated"
// about money sitting on the booking in front of them. 217 of 758 inbound
// booking-counterparty payments in prod carry no key at all, and 30 bookings
// have a keyed and an unkeyed payment side by side (2026-11-01).
export function paidByLine(payments, booking) {
  const targets = bookingToLineItems(booking)
    .map((item) => ({ key: item.source_key, owed: lineGross(item) }));
  return paidByOrderLine({ booking, payments, targets });
}

// Payment-driven document status (no manual status field). Every type derives
// from actual money received — including the Előlegszámla, which is only "paid"
// once the advance is collected. Heuristic at order level for this UI phase;
// the backend will key it off Payment.invoice_id for per-document precision.
//
// THIS IS THE SECOND COPY OF A RULE THAT LIVES IN
//   pickuphungary-dashboard-api/src/packages/ph-core/src/Helpers/invoiceStatus.helper.ts
//     → deriveInvoiceStatus
// and they are a PAIR: the API derives (and writes back) the status on every
// list, this derives it again for Order Details → Financial, which reads its
// documents straight off the order. Change one and change the other, or the
// same document reads differently on two screens — which is exactly what the
// Díjbekérő exclusion below was added to stop.
//
// A DÍJBEKÉRŐ KEEPS ITS OWN STORED STATUS AND IS NEVER DERIVED. It is a
// payment *request*: "its bookings are settled" says nothing about whether
// this request was paid, and our code deliberately does not register payment
// on a Díjbekérő in Billingo, so a derived 'paid' was our own guess with
// nothing behind it. Measured 2026-11-02: of 158 Díjbekérő rows, 107 are
// stored 'paid' and 27 of those were never converted at all. The derivation
// had also already overwritten Billingo's own answer on all 107 — there is no
// `billingo_status` field to fall back on — and ZERO rows survive as
// 'expired', which is why the 38 expired Díjbekérős were invisible.
export function deriveDocStatus(doc, orderPaidTotal = 0, orderValue = 0) {
  if (doc.status === 'cancelled') return 'cancelled';
  if (doc.type === DOC_TYPES.LEGACY) return 'uploaded';
  if (doc.type === DOC_TYPES.DIJBEKERO) return doc.status;
  // A final Számla is settled when the whole order value is paid; a Díjbekérő /
  // Előlegszámla when its own amount is covered.
  const target = doc.type === DOC_TYPES.SZAMLA ? orderValue : (Number(doc.total) || 0);
  if (target > 0 && orderPaidTotal >= target - 0.005) return 'paid';
  if (orderPaidTotal > 0.005) return 'partially_paid';
  return 'awaiting_payment';
}

// The synergy view: per-order-line owed / invoiced / paid / outstanding for one
// booking, combining its order_lines, Billingo documents and payments.
export function orderLineReconciliation(booking, documents, payments) {
  const lines = bookingToLineItems(booking);
  const invMap = invoicedByLine(documents);
  const paid = paidByLine(payments, booking);
  const rows = lines.map((line) => {
    const value = lineGross(line);
    const invoiced = invMap[line.key]?.invoiced || 0;
    const linePaid = paid.byKey[line.source_key] || 0;
    return {
      key: line.key,
      order_line_key: line.source_key,
      description: line.description,
      currency: line.currency,
      value,
      invoiced,
      paid: linePaid,
      uninvoiced: value - invoiced,
      outstanding: value - linePaid,
    };
  });
  const totals = rows.reduce((acc, r) => ({
    value: acc.value + r.value,
    invoiced: acc.invoiced + r.invoiced,
    paid: acc.paid + r.paid,
    outstanding: acc.outstanding + r.outstanding,
  }), {
    value: 0, invoiced: 0, paid: 0, outstanding: 0,
  });
  return {
    rows,
    // Received but absorbed by no line: an OVERPAYMENT under the one policy,
    // not the old "nobody keyed it" case. 128 bookings in prod, down from 970
    // (2026-11-01) — see orderLinePaid.js.
    unappliedPaid: paid.unapplied,
    totals,
    currency: rows[0]?.currency || booking?.currency || 'EUR',
  };
}

// Terms to look an existing Billingo partner up by, most precise first: the
// billing name is the party actually invoiced, then the email (unique in
// practice), then the passenger name.
export function accountSearchTerms(bookings = []) {
  const booking = (bookings || [])[0];
  if (!booking) return [];
  return [...new Set(
    [booking.billing?.name, booking.email_address, booking.full_name]
      .map((t) => (t || '').trim())
      .filter(Boolean),
  )];
}

// The Billingo account a previous document for these bookings was issued to.
// Same order, same party — the strongest signal we have that the customer
// already exists in Billingo. Cancelled documents still count: the party was
// real, only the document was voided.
export function priorAccountId(documents = []) {
  const withAccount = (documents || [])
    .filter((d) => d && d.billingo_account_id)
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  return withAccount[0]?.billingo_account_id ?? null;
}

// THE decision of which document an order needs next. The flow preselects it
// and the type picker badges it "Recommended", so the two can never disagree
// (they used to be computed separately and did). It follows the chain from
// the documents already live on the order:
//   - a final Számla, or a legacy uploaded invoice, exists → nothing: another
//     document would invoice the order twice
//   - the trip is done, or an advance exists → the final Számla
//   - money came in (launched from a payment), or a Díjbekérő exists
//     → Előlegszámla
//   - nothing yet → Díjbekérő
// `bookings` are the orders being invoiced; all Completed means the trip is
// rendered and the final invoice is due.
export function recommendedDocType(existingDocs = [], { mode = 'order', bookings = [] } = {}) {
  const types = new Set((existingDocs || []).filter(isLiveDocument).map((d) => d.type));
  if (types.has(DOC_TYPES.SZAMLA) || types.has(DOC_TYPES.LEGACY)) return null;
  const rendered = bookings.length > 0 && bookings.every((b) => b?.status === 'Completed');
  if (rendered || types.has(DOC_TYPES.ELOLEGSZAMLA)) return DOC_TYPES.SZAMLA;
  if (mode === 'payment' || types.has(DOC_TYPES.DIJBEKERO)) return DOC_TYPES.ELOLEGSZAMLA;
  return DOC_TYPES.DIJBEKERO;
}

// Money display, aligned with the dashboard's formatPaymentAmount convention
// (HUF: rounded + " Ft"; EUR: "€" + 2 decimals; other: amount + currency).
export function formatAmount(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const n = Number(amount);
  if (currency === 'HUF') return `${Math.round(n).toLocaleString('en-US')} Ft`;
  const formatted = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (currency === 'EUR') return `€${formatted}`;
  return currency ? `${formatted} ${currency}` : formatted;
}

// Compact money for a stat tile, where the exact cents never matter and a long
// number overflows: €12.1K, 284.8K Ft, €4.2M. Below 10,000 it stays exact.
export function formatAmountCompact(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const n = Number(amount);
  const abs = Math.abs(n);
  if (abs < 10000) return formatAmount(currency === 'HUF' ? Math.round(n) : n, currency);
  const [value, suffix] = abs >= 1000000 ? [n / 1000000, 'M'] : [n / 1000, 'K'];
  // One decimal, but never "12.0K" — the .0 is noise at this size.
  const short = `${Number(value.toFixed(1))}${suffix}`;
  if (currency === 'HUF') return `${short} Ft`;
  if (currency === 'EUR') return `€${short}`;
  return currency ? `${short} ${currency}` : short;
}

/**
 * The reason a `/billingo` call was refused, as the operator should read it.
 *
 * A ValidationError from the dashboard API answers with a CONSTANT
 * `message: 'The validation has failed'` and puts the real reason in
 * `errors[]` (ErrorResponseMiddleware). So reading `data.message` alone is
 * why a refused Számla showed up as "The validation has failed", with no sign
 * of Billingo's own
 *
 *   advance_invoice: Can not use advance(s) (137491737) for this document
 *   because it has descendant(s)!
 *
 * The operator had nothing to act on, so they clicked again — nineteen times
 * on one order group. Prefer the field errors, verbatim: Billingo's wording
 * names the document to go and check, and no paraphrase of ours would.
 *
 * Other error shapes are unaffected — a 409 carries its reason in `message`
 * and has no `errors`, so it falls straight through.
 */
export function apiErrorText(err, fallback = 'The request failed.') {
  const data = err?.response?.data;
  const fromErrors = (Array.isArray(data?.errors) ? data.errors : [])
    .map((e) => (e && typeof e.message === 'string' ? e.message.trim() : ''))
    .filter(Boolean)
    .join('; ');
  return fromErrors || data?.message || err?.message || fallback;
}
